/**
 * Company email template API URL + fetch options.
 * Split out for unit tests and to match CompanyService / EndpointTestingPanel prod vs dev behavior.
 */

import { getStoredSessionToken } from '../../utils/sessionToken'
import { getServerBaseUrl } from '@/utils/serverUrl'
import { forbiddenText } from '../../utils/rbacMessages'

export function companyTemplatesPath(companyId: string, suffix = ''): string {
  return `/api/email/company-templates/${encodeURIComponent(companyId)}${suffix}`
}

export function resolveCompanyTemplatesUrl(
  isDev: boolean,
  serverBaseUrl: string,
  companyId: string,
  suffix = '',
): string {
  const path = companyTemplatesPath(companyId, suffix)
  return isDev ? path : `${serverBaseUrl}${path}`
}

/**
 * In dev, omit credentials entirely — the Vite proxy keeps requests same-origin
 * and the server uses `Access-Control-Allow-Origin: *` which rejects `include`.
 * In prod, `include` is needed for cross-origin cookie/header passthrough.
 */
export function companyTemplatesCredentials(isDev: boolean): RequestCredentials | undefined {
  return isDev ? undefined : 'include'
}

/** Limits the server enforces (shown as helper text; the server is the authority). */
export const COMPANY_TEMPLATE_SUBJECT_MAX = 200
export const COMPANY_TEMPLATE_BODY_MAX = 5000
export const COMPANY_TEMPLATE_TEST_MAX_RECIPIENTS = 5

export interface CompanyTemplateOverride {
  id: string
  subjectOverride: string | null
  bodyText: string | null
  updatedAt: string
  updatedBy: string | null
}

export interface CompanyTemplateSlot {
  templateKey: string
  description: string
  /** Variables the server accepts as `{{name}}` in this template's text. */
  textVars: string[]
  hasOverride: boolean
  subject: string
  bodyText: string
  defaultSubject: string
  defaultBodyText: string
  override: CompanyTemplateOverride | null
}

export interface TestEmailResult {
  email: string
  success: boolean
  error?: string
}

/** Error from the company-templates API. `items` are the details or addresses to show as a list. */
export class CompanyTemplateApiError extends Error {
  code?: string
  items: string[]

  constructor(message: string, code?: string, items: string[] = []) {
    super(message)
    this.name = 'CompanyTemplateApiError'
    this.code = code
    this.items = items
  }
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string' && !!v) : []
}

export async function apiErrorFromResponse(
  res: Response,
  label: string,
): Promise<CompanyTemplateApiError> {
  const body = await res.json().catch(() => null)
  const serverMsg = body && typeof body.error === 'string' && body.error.trim() ? body.error.trim() : null
  const code = body && typeof body.code === 'string' ? body.code : undefined

  const forbidden = forbiddenText(res.status, body)
  if (forbidden) return new CompanyTemplateApiError(`${label}: ${forbidden}`, code)
  if (code === 'invalid_text') {
    return new CompanyTemplateApiError(
      `${label}: ${serverMsg || 'the text is not valid'}`,
      code,
      stringList(body.details),
    )
  }
  if (code === 'html_not_supported') {
    return new CompanyTemplateApiError(
      `${label}: HTML is not supported in company e-mail text. Use plain text only.`,
      code,
    )
  }
  if (code === 'not_member') {
    return new CompanyTemplateApiError(
      `${label}: these addresses are not active members of this company`,
      code,
      stringList(body.emails),
    )
  }
  if (serverMsg) return new CompanyTemplateApiError(`${label}: ${serverMsg}`, code)
  return new CompanyTemplateApiError(`${label}: ${res.statusText || String(res.status)}`, code)
}

export async function errorMessageFromResponse(res: Response, label: string): Promise<string> {
  return (await apiErrorFromResponse(res, label)).message
}

function isLocalhostHostname(): boolean {
  try {
    const h = window.location.hostname
    return h === 'localhost' || h === '127.0.0.1'
  } catch {
    return false
  }
}

function request(companyId: string, suffix: string, init: RequestInit = {}): Promise<Response> {
  const token = getStoredSessionToken()
  const headers: Record<string, string> = {}
  if (init.body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  const credentials = companyTemplatesCredentials(isLocalhostHostname())
  return fetch(resolveCompanyTemplatesUrl(false, getServerBaseUrl(), companyId, suffix), {
    ...init,
    headers,
    ...(credentials ? { credentials } : {}),
  })
}

const slotPath = (templateKey: string, tail = '') => `/${encodeURIComponent(templateKey)}${tail}`

export async function fetchCompanyTemplates(companyId: string): Promise<CompanyTemplateSlot[]> {
  const res = await request(companyId, '')
  if (!res.ok) throw await apiErrorFromResponse(res, 'Failed to load templates')
  const data = await res.json()
  return data.templates
}

/** Empty text is sent as null so the platform default applies. Never sends HTML or the removed HTML field. */
export async function saveCompanyTemplate(
  companyId: string,
  templateKey: string,
  subjectOverride: string,
  bodyText: string,
): Promise<void> {
  const res = await request(companyId, slotPath(templateKey), {
    method: 'PUT',
    body: JSON.stringify({
      subjectOverride: subjectOverride.trim() ? subjectOverride : null,
      bodyText: bodyText.trim() ? bodyText : null,
    }),
  })
  if (!res.ok) throw await apiErrorFromResponse(res, 'Save failed')
}

export async function revertCompanyTemplate(companyId: string, templateKey: string): Promise<void> {
  const res = await request(companyId, slotPath(templateKey), { method: 'DELETE' })
  if (!res.ok) throw await apiErrorFromResponse(res, 'Revert failed')
}

export async function previewCompanyTemplate(
  companyId: string,
  templateKey: string,
  subjectOverride: string,
  bodyText: string,
): Promise<{ subject: string; html: string; text?: string }> {
  const res = await request(companyId, slotPath(templateKey, '/preview'), {
    method: 'POST',
    body: JSON.stringify({
      subjectOverride: subjectOverride.trim() ? subjectOverride : undefined,
      bodyText: bodyText.trim() ? bodyText : undefined,
    }),
  })
  if (!res.ok) throw await apiErrorFromResponse(res, 'Preview failed')
  return res.json()
}

/**
 * Send a test for a company template slot. No addresses means the server sends to the caller's
 * own e-mail; every address must be an active member of the company (server-enforced).
 */
export async function sendCompanyTemplateTestEmail(
  companyId: string,
  templateKey: string,
  recipientEmails: string[] = [],
): Promise<{ success: boolean; results: TestEmailResult[] }> {
  const res = await request(companyId, slotPath(templateKey, '/test'), {
    method: 'POST',
    body: JSON.stringify(recipientEmails.length > 0 ? { recipientEmails } : {}),
  })
  if (!res.ok) throw await apiErrorFromResponse(res, 'Test send failed')
  return res.json()
}

/** Splits a comma/space separated address field into unique, trimmed addresses. */
export function parseRecipientEmails(input: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of input.split(/[,;\s]+/)) {
    const e = raw.trim()
    if (!e || seen.has(e.toLowerCase())) continue
    seen.add(e.toLowerCase())
    out.push(e)
  }
  return out
}
